import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Table, Button, Input, Tag, Alert, Space, message as antMessage } from 'antd';
import { SaveOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { fetchManageFreePlayers, saveFreePlayersBatch } from '../../../services/chargesApi';

const FreePlayersTab = ({ settingId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [chargeId, setChargeId] = useState(null);
  const [players, setPlayers] = useState([]);
  const [initialSet, setInitialSet] = useState(() => new Set());
  const [selectedSet, setSelectedSet] = useState(() => new Set());
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    if (!settingId) return;
    setLoading(true);
    try {
      const data = await fetchManageFreePlayers(settingId);
      const list = Array.isArray(data?.players) ? data.players : [];
      setChargeId(data?.charge_id || null);
      setPlayers(list);
      const initial = new Set(list.filter((p) => p.is_free).map((p) => p.id));
      setInitialSet(initial);
      setSelectedSet(new Set(initial));
    } catch {
      antMessage.error('Erro ao carregar jogadores');
    } finally {
      setLoading(false);
    }
  }, [settingId]);

  useEffect(() => { load(); }, [load]);

  const filteredPlayers = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return players;
    return players.filter((p) =>
      (p.name || '').toLowerCase().includes(s) ||
      (p.phone_number || '').toLowerCase().includes(s)
    );
  }, [players, search]);

  const isDirty = useMemo(() => {
    if (initialSet.size !== selectedSet.size) return true;
    for (const id of initialSet) {
      if (!selectedSet.has(id)) return true;
    }
    return false;
  }, [initialSet, selectedSet]);

  const handleToggle = (playerId, checked) => {
    setSelectedSet((prev) => {
      const next = new Set(prev);
      if (checked) next.add(playerId);
      else next.delete(playerId);
      return next;
    });
  };

  const handleSelectAllVisible = (selected) => {
    setSelectedSet((prev) => {
      const next = new Set(prev);
      for (const p of filteredPlayers) {
        if (selected) next.add(p.id);
        else next.delete(p.id);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      await saveFreePlayersBatch(settingId, Array.from(selectedSet));
      antMessage.success('Isenções salvas');
      await load();
    } catch (e) {
      antMessage.error(e?.response?.data?.message || 'Erro ao salvar isenções');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSelectedSet(new Set(initialSet));
  };

  const diffCount = useMemo(() => {
    let toAdd = 0, toRemove = 0;
    for (const id of selectedSet) if (!initialSet.has(id)) toAdd++;
    for (const id of initialSet) if (!selectedSet.has(id)) toRemove++;
    return { toAdd, toRemove };
  }, [selectedSet, initialSet]);

  if (!loading && !chargeId) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Esta cobrança ainda não gerou nenhuma fatura"
        description="Aguarde a primeira geração antes de marcar jogadores como isentos."
      />
    );
  }

  const columns = [
    { title: 'Jogador', dataIndex: 'name', width: 240 },
    { title: 'Telefone', dataIndex: 'phone_number', width: 170 },
    {
      title: 'Status',
      width: 130,
      render: (_, r) => {
        const wasFree = initialSet.has(r.id);
        const isFree = selectedSet.has(r.id);
        if (isFree && !wasFree) return <Tag color="blue">Será isento</Tag>;
        if (!isFree && wasFree) return <Tag color="orange">Removerá isenção</Tag>;
        if (isFree) return <Tag color="green">Isento</Tag>;
        return null;
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys: filteredPlayers.filter((p) => selectedSet.has(p.id)).map((p) => p.id),
    onSelect: (record, checked) => handleToggle(record.id, checked),
    onSelectAll: (selected) => handleSelectAllVisible(selected),
    onChange: () => {},
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Buscar por nome ou telefone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320, flex: 1 }}
        />
        <Space>
          {isDirty && (
            <span style={{ color: '#8c8c8c', fontSize: 13 }}>
              {diffCount.toAdd > 0 && <span>+{diffCount.toAdd} a isentar</span>}
              {diffCount.toAdd > 0 && diffCount.toRemove > 0 && <span> · </span>}
              {diffCount.toRemove > 0 && <span>-{diffCount.toRemove} a remover</span>}
            </span>
          )}
          <Button icon={<ReloadOutlined />} onClick={handleReset} disabled={!isDirty || saving}>
            Desfazer
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            disabled={!isDirty}
            loading={saving}
          >
            Salvar isenções
          </Button>
        </Space>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={filteredPlayers}
        columns={columns}
        rowSelection={rowSelection}
        pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        size="small"
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default FreePlayersTab;
